import React from 'react';
import { AFFILIATE } from '../config/affiliates';

interface Props {
  visaType: string;   // e.g. "Visa Required", "Visa-Free", "eVisa Required"
  passportCode: string;
  destinationCode: string;
  destinationName: string;
}

function ShieldIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

export default function AffiliateCTAs({ visaType, passportCode, destinationCode, destinationName }: Props) {
  const lower = visaType.toLowerCase();
  const needsVisa = lower.includes('required') || lower.includes('evisa') || lower.includes('e-visa') || lower.includes('arrival');

  return (
    <div className="px-6 py-5 border-b border-slate-100 no-print">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">
        Prepare for your trip
      </p>

      <div className="flex flex-wrap gap-2">

        {/* Travel insurance — always shown */}
        <a
          href={AFFILIATE.safetyWing()}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-xs font-semibold text-slate-700 hover:text-blue-700 transition-all group"
        >
          <span className="text-emerald-500 group-hover:text-emerald-600">
            <ShieldIcon />
          </span>
          Travel Insurance
          <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>

        {/* Visa application — only shown when visa needed */}
        {needsVisa && (
          <a
            href={AFFILIATE.ivisa(destinationCode)}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-xs font-semibold text-slate-700 hover:text-blue-700 transition-all group"
          >
            <span className="text-blue-500 group-hover:text-blue-600">
              <DocumentIcon />
            </span>
            Apply for Visa
            <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}

        {/* Flights deeplink — always shown */}
        <a
          href={AFFILIATE.skyscanner(passportCode, destinationCode)}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm text-xs font-semibold text-slate-700 hover:text-blue-700 transition-all group"
        >
          <span className="text-sky-500 group-hover:text-sky-600">
            <PlaneIcon />
          </span>
          Search Flights
          <svg className="w-3 h-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>

      </div>

      <p className="text-[10px] text-slate-300 mt-2.5">
        Sponsored links · We may earn a commission at no extra cost to you
      </p>
    </div>
  );
}
